import { signIn } from 'next-auth/react'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { createPassword } from '../services/createPassword'
import ChangePasswordForm from '../views/CreatePasswordForm'
import { getLanguageFromLocale } from '../../../util/getLanguageFromLocale'
import useAuthClaims from '../../../hooks/useAuthClaims'

export type CreatePasswordFormContainerProps = {
  token: string
}

const CreatePasswordFormContainer = ({
  token,
}: CreatePasswordFormContainerProps) => {
  const router = useRouter()
  const [error, setError] = useState<boolean>(false)
  const [tokenHasExpired, setTokenHasExpired] = useState<boolean>(false)
  const [userAlreadyExists, setUserAlreadyExists] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)

  const { claims } = useAuthClaims()
  const countryISO = claims?.countryISO || 'USA'
  const locale = countryISO === 'DK' ? 'da' : 'en'

  const language = getLanguageFromLocale(locale || 'en')

  const handleSubmit = async (password: string) => {
    setLoading(true)
    const statusCode = await createPassword(password, token, language)

    switch (statusCode) {
      case 200: // Success - password created successfully
        if (error) {
          setError(false)
        }
        await handleSignInClick()
        break
      case 401: // Unauthorized - token has expired or is invalid
        setError(false)
        setTokenHasExpired(true)
        break
      case 403: // Forbidden - access denied
        setError(false)
        // Handle forbidden access as needed
        break
      case 409: // Conflict - user already exists
        setError(false)
        setUserAlreadyExists(true)
        break
      default: // Any other error status code
        setError(true)
        break
    }

    setLoading(false)
  }

  const handleSignInClick = async () => {
    if (!loading) {
      setLoading(true)
    }
    await signIn('auth0', {
      callbackUrl: `/${router.locale}`,
    })
  }

  return (
    <ChangePasswordForm
      error={error}
      loading={loading}
      onSignInClick={handleSignInClick}
      onSubmit={handleSubmit}
      tokenHasExpired={tokenHasExpired}
      userAlreadyExists={userAlreadyExists}
    />
  )
}

export default CreatePasswordFormContainer
